// ─────────────────────────────────────────────────────────────────
// Usevisitsummary.js — dependent-aware v3
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useRef } from "react";
import axios from "axios";


// ── Helper: build endpoint based on patient/dependent ─────────
function patientPath(endpoint, dependentPrn, selfPrn) {
  if (dependentPrn && dependentPrn !== selfPrn) {
    return `/api/patient/dependent/${dependentPrn}/${endpoint}`;
  }

  return `/api/patient/${endpoint}`;
}


// ── useVisitSummary hook ──────────────────────────────────────
export function useVisitSummary(
  visitNumber,
  token,
  dependentPrn,
  selfPrn
) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cancelRef = useRef(null);


useEffect(() => {
  if (!visitNumber || !token) return;

  const loadSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const base = dependentPrn && dependentPrn !== selfPrn
        ? `/api/patient/dependent/${dependentPrn}`
        : `/api/patient`;


      const [
        consultation,
        vitals,
        prescriptions,
        labs
      ] = await Promise.all([
        axios.get(
          `${base}/consultation/${visitNumber}`,
          {
            headers:{
              Authorization:`Bearer ${token}`
            }
          }
        ),

        axios.get(
          `${base}/vitals/${visitNumber}`,
          {
            headers:{
              Authorization:`Bearer ${token}`
            }
          }
        ),

        axios.get(
          `${base}/prescriptions/${visitNumber}`,
          {
            headers:{
              Authorization:`Bearer ${token}`
            }
          }
        ),

        axios.get(
          `${base}/lab-results/${visitNumber}`,
          {
            headers:{
              Authorization:`Bearer ${token}`
            }
          }
        )
      ]);


      setData({
        success:true,

        consultation:
          consultation.data.data ||
          consultation.data.consultation,

        vitals:
          vitals.data.data,

        prescriptions:
          prescriptions.data.data,

        labs:
          labs.data.data
      });


    } catch(err){

      console.error(err);

      setError(
        err.response?.data?.message ||
        "Failed loading medical records"
      );

    } finally {
      setLoading(false);
    }
  };


  loadSummary();

},[
  visitNumber,
  token,
  dependentPrn,
  selfPrn
]);


  return {
    data,
    loading,
    error
  };
}



// ── fetchConsultation ─────────────────────────────────────────
export async function fetchConsultation(
  visitNumber,
  token,
  dependentPrn,
  selfPrn
) {

  const url = patientPath(
    `consultation/${visitNumber}`,
    dependentPrn,
    selfPrn
  );


  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return res.data;
}



// ── fetchVitals ───────────────────────────────────────────────
export async function fetchVitals(
  visitNumber,
  token,
  dependentPrn,
  selfPrn
) {

  const url = patientPath(
    `vitals/${visitNumber}`,
    dependentPrn,
    selfPrn
  );


  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return res.data;
}



// ── fetchPrescriptions ────────────────────────────────────────
export async function fetchPrescriptions(
  visitNumber,
  token,
  dependentPrn,
  selfPrn
) {

  const url = patientPath(
    `prescriptions/${visitNumber}`,
    dependentPrn,
    selfPrn
  );


  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });


  return res.data;
}



// ── fetchLabResults ───────────────────────────────────────────
export async function fetchLabResults(
  visitNumber,
  token,
  dependentPrn,
  selfPrn
) {

  const url = patientPath(
    `lab-results/${visitNumber}`,
    dependentPrn,
    selfPrn
  );


  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });


  return res.data;
}



// ── fetchDependentVisits ──────────────────────────────────────
export async function fetchDependentVisits(
  prn,
  token
) {

  const res = await axios.get(
    `/api/patient/dependent/${prn}/visits`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return res.data;
}



// ── fetchDependentAppointments ────────────────────────────────
export async function fetchDependentAppointments(
  prn,
  visitNumber,
  token
) {

  const res = await axios.get(
    `/api/patient/dependent/${prn}/appointments/${visitNumber}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return res.data;
}



// ── fetchDependentProfile ─────────────────────────────────────
export async function fetchDependentProfile(
  prn,
  token
) {

  const res = await axios.get(
    `/api/patient/dependent/${prn}/me`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return res.data;
}